import React, { useCallback, useEffect, useState } from "react";
import { SummaryExpr } from "@aidkitorg/types/lib/survey";
import { generateDistroSummaryKind } from "../../../utils/adminPanel.utils";
import { DashboardMetric } from "@aidkitorg/types/lib/admin-panel/dashboard";
import { HeroNumberWithFields } from "../../../Contexts/DashboardsContext";

export const useMetricFormState = (component?: DashboardMetric) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  const [metricType, setMetricType] = useState('numeric');
  const [aggregation, setAggregation] = useState('Sum');
  const [dataField, setDataField] = useState('');

  const [numeratorAggregation, setNumeratorAggregation] = useState('Count');
  const [numerator, setNumerator] = useState('');
  const [denominatorAggregation, setDenominatorAggregation] = useState('Count');
  const [denominator, setDenominator] = useState('');

  const [decimalPlaces, setDecimalPlaces] = useState(2);

  useEffect(() => {
    if (!component) return;

    const componentTitle = 'title' in component && component.title
      ? typeof component.title === 'string'
        ? component.title
        : component.title['en']
      : 'Dashboard Component';

    setTitle(componentTitle);
    setDescription(component.description || '');
    setAggregation(component.summary.kind);
    setDecimalPlaces(component.decimalPlaces ?? 2);

    if (component.suffix === '%' || component.summary.kind === 'Percentage') {
      setMetricType('percentage');
    } else if (component.prefix === '$') {
      setMetricType('currency');
    } else {
      setMetricType('numeric');
    }

    if (component.summary.kind === 'Percentage') {
      const { numerator, denominator } = component.summary;

      setNumeratorAggregation(numerator.kind);
      if (numerator.kind === 'Mean' || numerator.kind === 'Sum') {
        const numeratorValue = numerator.value
        
        // We need to assert that this is a field type
        if (numeratorValue.kind === 'Field') {
          setNumerator(numeratorValue.field)
        }
      }

      setDenominatorAggregation(denominator.kind);
      if (denominator.kind === 'Mean' || denominator.kind === 'Sum') {
        const denominatorValue = denominator.value
        
        // We need to assert that this is a field type
        if (denominatorValue.kind === 'Field') {
          setDenominator(denominatorValue.field)
        }
      }
    } else if (component.summary.kind === 'Sum' || component.summary.kind === 'Mean') {
      if (component.summary.value.kind === 'Field') {
        setDataField(component.summary.value.field);
      }
    }
  }, [component]);

  const generatePercentageSummaryKind = useCallback((): SummaryExpr => ({
    kind: 'Percentage',
    numerator: generateDistroSummaryKind(numeratorAggregation, numerator),
    denominator: generateDistroSummaryKind(denominatorAggregation, denominator),
  }), [numeratorAggregation, numerator, denominatorAggregation, denominator]);

  const generateSummary = (): SummaryExpr =>
    metricType === 'percentage'
      ? generatePercentageSummaryKind()
      : generateDistroSummaryKind(aggregation, dataField);

  const buildMetricObject = (): HeroNumberWithFields => {
    const metric: HeroNumberWithFields = {
      kind: 'Hero Number',
      title,
      summary: generateSummary(),
      useCommas: true,
      description,
    };

    if (metricType === 'percentage') {
      metric.suffix = '%';
    } else if (metricType === 'currency') {
      metric.prefix = '$';
      metric.decimalPlaces = 2;
    }

    // Set decimal places for Average (Mean) aggregation
    if (aggregation === 'Mean') {
      metric.decimalPlaces = decimalPlaces;
    }

    return metric;
  };

  const percentageValid = (numeratorAggregation === 'Count' || numerator !== '') && (denominatorAggregation === 'Count' || denominator !== '')
  const nonPercentageValid = dataField !== '';
  const isFormStateValid = title !== '' &&
    (
      (metricType === 'percentage' && percentageValid) ||
      (metricType !== 'percentage' && nonPercentageValid)
    )
  
  return {
    title, setTitle,
    description, setDescription,
    metricType, setMetricType,
    aggregation, setAggregation,
    dataField, setDataField,
    numeratorAggregation, setNumeratorAggregation,
    numerator, setNumerator,
    denominatorAggregation, setDenominatorAggregation,
    denominator, setDenominator,
    decimalPlaces, setDecimalPlaces,
    buildMetricObject,
    isFormStateValid
  };
};
